/**
 * ConsolidationPackagesTab — Sprint 3 (Feb 2026).
 *
 * Operationele Pakketten-view: toont alle aan de consolidatie gekoppelde
 * shipments met de exacte kolommen die spec Sprint 3 §4 vereist. Deze
 * tab is read-only en dient als operationeel referentie-scherm; add/
 * remove/edit blijft in de "Zendingen" beheer-tab.
 *
 * Data-bron: `data.shipments` van de parent (single fetch — géén dubbele
 * polling). Klantnaam en operational_status komen uit de aangepaste
 * backend row-projector.
 *
 * Row-klik → opent volledig ShipmentDetail via bestaande deeplink helper
 * `buildShipmentUrl({wNumber})` (spec Sprint 3 §6).
 */
import React from "react";
import { Boxes, ExternalLink, Truck, Ship, Plane, Package } from "lucide-react";
import { Button } from "../../ui/button";
import ServiceTypeBadge from "./ServiceTypeBadge";
import DestinationBadge from "./DestinationBadge";
import { buildShipmentUrl } from "../../../utils/shipmentNavigation";

function fmtNum(n, digits = 2) {
  return Number(n || 0).toFixed(digits).replace(".", ",");
}

const PAYMENT_STATUS_LABELS_NL = {
  paid: "Betaald",
  not_paid: "Openstaand",
  unpaid: "Openstaand",
  partial: "Gedeeltelijk",
  partially_paid: "Gedeeltelijk",
  waived: "Kwijtgescholden",
  released: "Vrijgegeven",
  payment_required: "Betaling vereist",
};

const PAYMENT_STATUS_COLORS = {
  paid: "bg-emerald-100 text-emerald-800",
  released: "bg-emerald-100 text-emerald-800",
  waived: "bg-emerald-100 text-emerald-800",
  not_paid: "bg-red-100 text-red-800",
  unpaid: "bg-red-100 text-red-800",
  partial: "bg-amber-100 text-amber-900",
  partially_paid: "bg-amber-100 text-amber-900",
  payment_required: "bg-amber-100 text-amber-900",
};

const STATUS_LABELS_NL = {
  aangemaakt: "Klaar voor verwerking",
  concept: "Concept",
  gedownload: "Klaar voor verwerking",
  consolidatie_aangemaakt: "In consolidatie",
  in_warehouse_ontvangen: "In magazijn",
  container_samengesteld: "Container geladen",
  consolidatie_gesloten: "Consolidatie gesloten",
  vertrokken: "Vertrokken",
  onderweg: "Onderweg",
  aangekomen: "Aangekomen",
  douane: "Douane",
  vrijgegeven: "Vrijgegeven",
  klaar_voor_afhalen: "Klaar voor afhalen",
  delivery_ingepland: "Bezorging ingepland",
  afgeleverd: "Afgeleverd",
  afgesloten: "Afgesloten",
  geannuleerd: "Geannuleerd",
};

function StatusPill({ status }) {
  const label = STATUS_LABELS_NL[status] || status || "-";
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-slate-100 text-slate-700 border border-slate-200"
      data-testid="packages-row-status"
    >
      {label}
    </span>
  );
}

function PaymentPill({ status }) {
  if (!status) return <span className="text-slate-400 text-[10px]">—</span>;
  const label = PAYMENT_STATUS_LABELS_NL[status] || status;
  const color = PAYMENT_STATUS_COLORS[status] || "bg-slate-100 text-slate-700";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase ${color}`}
      data-testid="packages-row-payment"
    >
      {label}
    </span>
  );
}

function TransportModeIcon({ serviceType }) {
  const s = (serviceType || "").toLowerCase();
  if (s.startsWith("air") || s.includes("_air")) return <Plane className="w-3 h-3" />;
  if (s.startsWith("sea") || s.includes("_sea")) return <Ship className="w-3 h-3" />;
  if (s === "courier") return <Truck className="w-3 h-3" />;
  return <Package className="w-3 h-3" />;
}

export default function ConsolidationPackagesTab({ data }) {
  const shipments = (data && data.shipments) || [];

  const openShipment = (w) => {
    if (!w) return;
    window.location.assign(buildShipmentUrl({ wNumber: w, source: "consolidation-packages" }));
  };

  if (shipments.length === 0) {
    return (
      <div
        className="text-center py-14 text-slate-500 border border-dashed border-slate-200"
        data-testid="packages-empty"
      >
        <Boxes className="w-10 h-10 mx-auto mb-2 text-slate-300" />
        <p className="text-sm font-medium">Geen zendingen aan deze consolidatie gekoppeld.</p>
        <p className="text-xs mt-1 text-slate-400">
          Voeg zendingen toe via de tab &quot;Zendingen&quot;.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="packages-tab-root">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        {shipments.length} pakket{shipments.length === 1 ? "" : "ten"} in deze consolidatie
      </div>
      <div className="overflow-x-auto border border-gray-200">
        <table className="w-full text-xs min-w-[1250px]" data-testid="packages-table">
          <thead className="bg-gray-50 text-[10px] uppercase font-bold text-gray-600">
            <tr>
              <th className="text-left p-2">W-nummer</th>
              <th className="text-left p-2">Klant</th>
              <th className="text-left p-2">Ontvanger</th>
              <th className="text-left p-2 min-w-[180px]">Omschrijving</th>
              <th className="text-right p-2">Colli</th>
              <th className="text-right p-2">Gewicht</th>
              <th className="text-right p-2">CBM</th>
              <th className="text-left p-2">Transport</th>
              <th className="text-left p-2">Bestemming</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Betaalstatus</th>
              <th className="text-right p-2">Acties</th>
            </tr>
          </thead>
          <tbody>
            {shipments.map((s) => {
              const w = s.w_number || s.tracking_number;
              return (
                <tr
                  key={s.link_id || w}
                  className="border-t border-gray-100 hover:bg-blue-50/30"
                  data-testid={`packages-row-${w}`}
                >
                  <td className="p-2 font-mono font-bold text-[#082c50]" data-testid={`packages-row-wnumber-${w}`}>
                    {w || "—"}
                  </td>
                  <td className="p-2" data-testid={`packages-row-customer-${w}`}>
                    <span className="text-slate-800">{s.customer_name || s.sender_name || "—"}</span>
                  </td>
                  <td className="p-2">
                    <div className="text-slate-800">{s.recipient_name || "—"}</div>
                    {s.recipient_city && (
                      <div className="text-[10px] text-slate-500">{s.recipient_city}</div>
                    )}
                  </td>
                  <td className="p-2 max-w-[280px]">
                    <div className="text-slate-700 truncate" title={s.description}>
                      {s.description || <span className="text-slate-400">—</span>}
                    </div>
                  </td>
                  <td className="p-2 text-right font-mono">{s.boxes || 0}</td>
                  <td className="p-2 text-right font-mono">{fmtNum(s.weight_kg, 1)} kg</td>
                  <td className="p-2 text-right font-mono">{fmtNum(s.volume_cbm, 4)}</td>
                  <td className="p-2">
                    <div className="flex items-center gap-1 text-slate-700">
                      <TransportModeIcon serviceType={s.service_type} />
                      <ServiceTypeBadge
                        serviceType={s.service_type}
                        testId={`packages-row-service-${w}`}
                      />
                    </div>
                  </td>
                  <td className="p-2">
                    <DestinationBadge
                      code={s.destination_country_code}
                      name={s.destination_country_name}
                      testId={`packages-row-destination-${w}`}
                    />
                  </td>
                  <td className="p-2">
                    <StatusPill status={s.operational_status || s.tracking_status || s.label_status} />
                  </td>
                  <td className="p-2">
                    <PaymentPill status={s.payment_status} />
                  </td>
                  <td className="p-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openShipment(w)}
                      className="h-7 rounded-none text-[10px] uppercase font-bold border-[#082c50] text-[#082c50] hover:bg-[#082c50] hover:text-white"
                      data-testid={`packages-open-shipment-${w}`}
                      disabled={!w}
                      title="Open zending in nieuw scherm"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" /> Open
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
